import { create } from 'zustand';
import {
  getRunStageContext,
  updateExecuteStep,
  updateRunDestiny,
  updateRunPlan,
  abortRun,
} from '@services/runStageApi.service';
import {
  bootstrapRunDashboard,
  getRunUIElements,
  pauseRunFull,
  resumeRunFull,
  executeGetV2,
} from '@services/runApi.service';
import type {
  ExecuteEntity,
  PlanEntity,
  RunStageContext,
  RunUIPermissions,
  UpdateExecuteStepRequest,
  UpdateRunDestinyRequest,
  UpdateRunPlanRequest,
} from '@/types/run-stage.types';

type RunStageStatus = 'idle' | 'loading' | 'ready' | 'error';

interface RunStageStoreState {
  runId: string | null;
  context: RunStageContext | null;
  uiPermissions: RunUIPermissions | null;
  status: RunStageStatus;
  error: string | null;
  lastLoadedAt: number | null;
  clear: () => void;
  hydrate: (runId: string) => Promise<RunStageContext>;
  refresh: () => Promise<RunStageContext | null>;
  fetchUIPermissions: (runId: string) => Promise<RunUIPermissions | null>;
  applyPlanUpdate: (payload: UpdateRunPlanRequest) => Promise<PlanEntity>;
  applyDestinyUpdate: (payload: UpdateRunDestinyRequest) => Promise<PlanEntity>;
  applyExecuteStepUpdate: (payload: UpdateExecuteStepRequest) => Promise<ExecuteEntity>;
  applyPauseRun: (reason?: string) => Promise<void>;
  applyResumeRun: (reason?: string) => Promise<void>;
  applyAbortRun: (reason?: string) => Promise<void>;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * For v2 runs (operationsInNodes / maxgraph), fetch the enriched OperationVertex
 * data from the execute endpoint. These vertices carry parameters and checkPoints
 * directly from the executeGraph, so the UI can render and edit them without a
 * separate graph fetch.
 *
 * Returns an empty array if execute doesn't exist yet or the call fails.
 */
async function fetchExecuteGraphVertices(runId: string): Promise<unknown[]> {
  try {
    const result = await executeGetV2({ runId });
    const steps: unknown[] = (result as any)?.data?.steps ?? [];
    return steps
      .map((s: unknown) => (s as any)?.meta)
      .filter(Boolean);
  } catch {
    return [];
  }
}

export const useRunStageStore = create<RunStageStoreState>((set, get) => ({
  runId: null,
  context: null,
  uiPermissions: null,
  status: 'idle',
  error: null,
  lastLoadedAt: null,

  clear: () => {
    set({
      runId: null,
      context: null,
      uiPermissions: null,
      status: 'idle',
      error: null,
      lastLoadedAt: null,
    });
  },

  hydrate: async (runId: string) => {
    set({ runId, status: 'loading', error: null });
    try {
      // Mirror RunResolver: fetch run + plan + execute + UI permissions in parallel
      const bootstrap = await bootstrapRunDashboard(runId);
      const runEntity = bootstrap.runInfo.entity;

      // For v2 runs, fetch enriched execute vertices (parameters + checkpoints from executeGraph)
      const isV2 = Boolean((runEntity as any).operationsInNodes) ||
        (runEntity as any).sourceEditor === 'maxgraph';
      const enrichedExecuteVertices = isV2 && bootstrap.execute
        ? await fetchExecuteGraphVertices(runId)
        : [];

      const context = {
        run: runEntity as import('@/types/run-stage.types').RunEntity,
        plan: bootstrap.plan,
        execute: bootstrap.execute,
        uiPermissions: bootstrap.uiElements,
        // Plan graph edges: prefer runInfo.entity.graph.edges (populated by /api/run/get/run),
        // fall back to separate planGraph fetch
        planGraphEdges: (
          (runEntity as any).graph?.edges ??
          bootstrap.planGraph?.graph?.edges ??
          []
        ) as import('@/types/run-stage.types').RunStageGraphEdge[],
        // Execute graph edges: from runInfo.entity.executeGraph.edges (populated by /api/run/get/run)
        executeGraphEdges: (
          (runEntity as any).executeGraph?.edges ?? []
        ) as import('@/types/run-stage.types').RunStageGraphEdge[],
        // Execute graph vertices: enriched from v2 execute endpoint (parameters + checkPoints)
        executeGraphVertices: enrichedExecuteVertices.length > 0
          ? enrichedExecuteVertices
          : (runEntity as any).executeGraph?.vertices ?? [],
        // Plan graph vertices
        planGraphVertices: (
          (runEntity as any).graph?.vertices ??
          bootstrap.planGraph?.graph?.vertices ??
          []
        ) as unknown[],
      };
      set({
        runId,
        context,
        uiPermissions: bootstrap.uiElements,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });
      return context;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load run stage context');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  refresh: async () => {
    const { runId, context: currentContext } = get();
    if (!runId) return null;

    set({ status: 'loading', error: null });
    try {
      const entityType = (currentContext?.run as any)?.version?.experiment?.fileType;
      const bootstrap = await bootstrapRunDashboard(runId, entityType);
      const runEntity = bootstrap.runInfo.entity;

      // For v2 runs, fetch enriched execute vertices (parameters + checkpoints from executeGraph)
      const isV2 = Boolean((runEntity as any).operationsInNodes) ||
        (runEntity as any).sourceEditor === 'maxgraph';
      const enrichedExecuteVertices = isV2 && bootstrap.execute
        ? await fetchExecuteGraphVertices(runId)
        : [];

      const context = {
        run: runEntity as import('@/types/run-stage.types').RunEntity,
        plan: bootstrap.plan,
        execute: bootstrap.execute,
        uiPermissions: bootstrap.uiElements,
        planGraphEdges: (
          (runEntity as any).graph?.edges ??
          bootstrap.planGraph?.graph?.edges ??
          []
        ) as import('@/types/run-stage.types').RunStageGraphEdge[],
        executeGraphEdges: (
          (runEntity as any).executeGraph?.edges ?? []
        ) as import('@/types/run-stage.types').RunStageGraphEdge[],
        executeGraphVertices: enrichedExecuteVertices.length > 0
          ? enrichedExecuteVertices
          : (runEntity as any).executeGraph?.vertices ?? [],
        planGraphVertices: (
          (runEntity as any).graph?.vertices ??
          bootstrap.planGraph?.graph?.vertices ??
          []
        ) as unknown[],
      };
      set({
        context,
        uiPermissions: bootstrap.uiElements,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });
      return context;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to refresh run stage context');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  applyPlanUpdate: async (payload: UpdateRunPlanRequest) => {
    set({ status: 'loading', error: null });
    try {
      const plan = await updateRunPlan(payload);
      const runId = payload.runId || get().runId;

      if (!runId) {
        set({ status: 'ready' });
        return plan;
      }

      const context = await getRunStageContext(runId);
      set({
        runId,
        context,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });

      return context.plan ?? plan;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update plan');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  applyDestinyUpdate: async (payload: UpdateRunDestinyRequest) => {
    set({ status: 'loading', error: null });
    try {
      const plan = await updateRunDestiny(payload);
      const runId = payload.runId || get().runId;

      if (!runId) {
        set({ status: 'ready' });
        return plan;
      }

      const context = await getRunStageContext(runId);
      set({
        runId,
        context,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });

      return context.plan ?? plan;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update destiny');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  applyExecuteStepUpdate: async (payload: UpdateExecuteStepRequest) => {
    set({ status: 'loading', error: null });
    try {
      const execute = await updateExecuteStep(payload);
      const runId = payload.runId || get().runId;

      if (!runId) {
        set({ status: 'ready' });
        return execute;
      }

      const context = await getRunStageContext(runId);
      set({
        runId,
        context,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });

      return context.execute ?? execute;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update execute step');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  fetchUIPermissions: async (runId: string) => {
    try {
      // Use the real /api/run/ui?action=runDetails endpoint (same as Angular refreshUIElements)
      const context = get().context;
      const entityType =
        (context?.run as any)?.version?.experiment?.fileType ?? undefined;
      const uiPermissions = await getRunUIElements(runId, entityType);
      set({ uiPermissions });
      return uiPermissions;
    } catch {
      return null;
    }
  },

  applyPauseRun: async () => {
    const { runId, context } = get();
    if (!runId) return;
    set({ status: 'loading', error: null });
    try {
      const runUid = context?.run?.uid ?? '';
      const entityType = (context?.run as any)?.version?.experiment?.fileType;
      // Mirror Angular pauseRun(): updateStatus + updateTimings then refreshUIElements
      await pauseRunFull(runId, runUid, entityType);
      const bootstrap = await bootstrapRunDashboard(runId, entityType);
      set({
        context: { run: bootstrap.runInfo.entity as import('@/types/run-stage.types').RunEntity, plan: bootstrap.plan, execute: bootstrap.execute },
        uiPermissions: bootstrap.uiElements,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to pause run');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  applyResumeRun: async () => {
    const { runId, context } = get();
    if (!runId) return;
    set({ status: 'loading', error: null });
    try {
      const runUid = context?.run?.uid ?? '';
      const entityType = (context?.run as any)?.version?.experiment?.fileType;
      // Mirror Angular resumeRun(): updateStatus + updateTimings then refreshUIElements
      await resumeRunFull(runId, runUid, entityType);
      const bootstrap = await bootstrapRunDashboard(runId, entityType);
      set({
        context: { run: bootstrap.runInfo.entity as import('@/types/run-stage.types').RunEntity, plan: bootstrap.plan, execute: bootstrap.execute },
        uiPermissions: bootstrap.uiElements,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to resume run');
      set({ status: 'error', error: message });
      throw error;
    }
  },

  applyAbortRun: async () => {
    const { runId, context } = get();
    if (!runId) return;
    set({ status: 'loading', error: null });
    try {
      const entityType = (context?.run as any)?.version?.experiment?.fileType;
      // Angular abort uses a modal then navigates to /summary; here we just call the API
      await abortRun({ runId });
      const bootstrap = await bootstrapRunDashboard(runId, entityType);
      set({
        context: { run: bootstrap.runInfo.entity as import('@/types/run-stage.types').RunEntity, plan: bootstrap.plan, execute: bootstrap.execute },
        uiPermissions: bootstrap.uiElements,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
      });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to abort run');
      set({ status: 'error', error: message });
      throw error;
    }
  },
}));
